import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppCircularProgress } from '../../../../components/common/loaders/AppCircularProgress';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Grid,
  IconButton,
  Typography,
  Link,
  Divider,
  Chip,
} from '@mui/material';
import GoogleMapView from '../../map/GoogleMapView';
import { CustomDialog } from '../../../../../src/components/CustomDialog';
import { useGetFieldDataForFieldNosQuery } from '../../../../services/mVPMapService';
import dayjs from 'dayjs';
import {
  AllowedDayType,
  MVPFormData,
  polygonType,
} from '../../constants';
import MapOnDialogView from '../../map/mapDialog';
// import { skipToken } from '@reduxjs/toolkit/query';
import { fromUrl } from 'geotiff';
import proj4 from 'proj4';
import {
  colorMapping,
  isPointInPolygon,
} from '../../map/tiff/helper';
import { latLngConversion } from '../../../../utils/functions/maps';
import { TimeSeriesPlot } from '../../charts/lineChart';
import { TDiscountTab } from '../../../../utils/types';
import { CustomTab } from '../../../../components/customTab';
import { useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './index.scss';
import ParentSeedData from './parentSeed';
import {
  useGetCheckInHistoryQuery,
  useGetFieldLogsByFieldNumberQuery,
} from '../../../../services/mVPGraphQlService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { useAreaUnit } from '../../../../utils/functions/countryUtils';
import ReportsCard from './reportsCard';
import FlagIcon from '@mui/icons-material/Flag';
import detasslingIcon from '../../../../assets/icons/detasslingIcon.svg';
import flagIcon from '../../../../assets/icons/flagIcon.svg';
import yieldEstimateIcon from '../../../../assets/icons/yieldEstimate.svg';
import statusIcon from '../../../../assets/icons/statusIcon.svg';
import harvestIcon from '../../../../assets/icons/harvestIcon.svg';
import mapStatic from '../../../../assets/icons/mapStatic.png';
import cultivationMethod from '../../../../assets/icons/cultivationMethod.svg';
import rowRatio from '../../../../assets/icons/rowRatio.svg';
import staggering from '../../../../assets/icons/staggering.svg';
import cropRating from '../../../../assets/icons/cropRating.svg';
import populationCount from '../../../../assets/icons/populationCount.svg';
import isolationDistance from '../../../../assets/icons/isolationDistance.svg';
import nickStatus from '../../../../assets/icons/nickStatus.svg';

import {
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
type TProps = {
  commonFilters: MVPFormData;
  fieldNumber: number;
  allowedDays: AllowedDayType[];
  fromReport?: boolean;
  setCenterValue?: any;
  sowingTimeInSeconds?: number;
  havestTimeInSeconds?: number;
};
// Static TIFF Image Component for Carousel
const StaticTiffImages: React.FC<{
  tiffFileUrls: string[];
  polygon: polygonType[];
  indexName?: string | null;
  onSelectImage: (index: number) => void;
  selectedIndex: number;
  staticMapImage?: string;
  allowedDays?: any;
}> = ({
  tiffFileUrls,
  polygon,
  indexName,
  onSelectImage,
  selectedIndex,
  staticMapImage,
  allowedDays,
}) => {
  const [staticImages, setStaticImages] = useState<
    string[]
  >([]);

  useEffect(() => {
    const renderStaticImages = async () => {
      const images: string[] = [];
      for (let i = 0; i < tiffFileUrls.length; i++) {
        try {
          const tiff = await fromUrl(tiffFileUrls[i]);
          const image = await tiff.getImage();
          const width = image.getWidth();
          const height = image.getHeight();
          const tiePoints = image.getTiePoints();
          const pixelScale =
            image.getFileDirectory().ModelPixelScale;

          const topLeftProjected = [
            tiePoints[0].x,
            tiePoints[0].y,
          ];
          const bottomRightProjected = [
            tiePoints[0].x + pixelScale[0] * width,
            tiePoints[0].y - pixelScale[1] * height,
          ];

          const topLeft = proj4(
            'EPSG:3857',
            'EPSG:4326',
            topLeftProjected
          );
          const bottomRight = proj4(
            'EPSG:3857',
            'EPSG:4326',
            bottomRightProjected
          );

          const clippingPrecisionFactor = 16;
          const canvasWidth =
            width * clippingPrecisionFactor;
          const canvasHeight =
            height * clippingPrecisionFactor;

          const canvas = document.createElement('canvas');
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          const context = canvas.getContext('2d');

          if (!context)
            throw new Error(
              'Failed to get 2D canvas context'
            );

          const imageData = context.createImageData(
            canvasWidth,
            canvasHeight
          );
          const data = imageData.data;
          const rasterData = await image.readRasters();
          const rasterDataAtPixels = rasterData[0];

          const startLat = topLeft[1];
          const startLng = topLeft[0];
          const latScale =
            (bottomRight[1] - topLeft[1]) / canvasHeight;
          const lngScale =
            (bottomRight[0] - topLeft[0]) / canvasWidth;

          for (
            let j = 0;
            j < canvasWidth * canvasHeight;
            j++
          ) {
            const colIndex = j % canvasWidth;
            const rowIndex = (j - colIndex) / canvasWidth;
            const currPixelLatLng = {
              lat: startLat + latScale * rowIndex,
              lng: startLng + lngScale * colIndex,
            };
            const isPointInsidePolygon = isPointInPolygon(
              currPixelLatLng,
              polygon
            );
            const actualRowIndex = Math.floor(
              rowIndex / clippingPrecisionFactor
            );
            const actualColIndex = Math.floor(
              colIndex / clippingPrecisionFactor
            );
            const rasterDataIndex =
              actualRowIndex * width + actualColIndex;
            const rasterDataAtCurrIndex =
              rasterDataAtPixels[rasterDataIndex];
            const color = colorMapping(
              indexName,
              rasterDataAtCurrIndex
            );
            data[j * 4] = color.R;
            data[j * 4 + 1] = color.G;
            data[j * 4 + 2] = color.B;
            data[j * 4 + 3] = isPointInsidePolygon
              ? 255
              : 0;
          }

          context.putImageData(imageData, 0, 0);
          images.push(canvas.toDataURL());
        } catch (error) {
          console.error(
            'Error rendering static TIFF:',
            error
          );
        }
      }

      setStaticImages(images);
    };

    if (tiffFileUrls.length > 0 || staticMapImage) {
      renderStaticImages();
    }
  }, [tiffFileUrls, polygon, indexName, staticMapImage]);

  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 250; // adjust scroll per click
      scrollRef.current?.scrollBy({
        left:
          direction === 'left'
            ? -scrollAmount
            : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        background: 'rgba(0,0,0,0.65)', // dark background
        //    borderRadius: '10px',
        padding: '20px ',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        height: '146px', //150
        marginTop: -18,
      }}>
      {/* Left Arrow */}
      <IconButton
        onClick={() => scroll('left')}
        sx={{
          position: 'absolute',
          left: 10,
          color: '#fff',
          background: 'rgba(255,255,255,0.1)',
          '&:hover': {
            background: 'rgba(255,255,255,0.25)',
          },
        }}>
        <ChevronLeft />
      </IconButton>
      {staticImages?.length > 0 ? (
        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: '18px',
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            padding: '10px',
            '&::-webkit-scrollbar': { display: 'none' },
          }}>
          {staticImages.map((imageUrl, index) => {
            const dayLabel =
              allowedDays?.[index]?.day ?? '';
            const isSelected = selectedIndex === index;

            return (
              <Box
                key={index}
                sx={{
                  flex: '0 0 auto',
                  // borderRadius: '12px',
                  padding: '20px', // space for outer box
                  backgroundColor: isSelected
                    ? '#000'
                    : 'transparent', // black box for selected
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: '#000', // black box on hover
                  },
                  '&::after': isSelected
                    ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '8px',
                        backgroundColor:
                          'rgba(9,144,153,1)', // blue top strip
                        // borderTopLeftRadius: '10px',
                        // borderTopRightRadius: '10px',
                      }
                    : {},
                }}
                onClick={() => onSelectImage(index)}>
                <Box
                  sx={{
                    width: '94px', //110
                    height: '114px', //130px
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    position: 'relative',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: '88px', //100
                      padding: '10px', // image padding
                      backgroundImage: `url(${mapStatic})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative',
                      borderBottom: '1px solid #eee',
                      boxSizing: 'border-box',
                    }}>
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={`Static TIFF ${index + 1}`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain', // ensures image stays within padded area
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        opacity: 0.9,
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      textAlign: 'center',
                      padding: '6px 0',
                      fontWeight: 600,
                      color: '#000',
                      fontSize: '14px',
                    }}>
                    {dayLabel}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <text style={{ color: 'white' }}>
          No data available
        </text>
      )}
      {/* Right Arrow */}
      <IconButton
        onClick={() => scroll('right')}
        sx={{
          position: 'absolute',
          right: 10,
          color: '#fff',
          background: 'rgba(255,255,255,0.1)',
          '&:hover': {
            background: 'rgba(255,255,255,0.25)',
          },
        }}>
        <ChevronRight />
      </IconButton>
    </Box>
  );
};

export const MapSection: React.FC<TProps> = ({
  commonFilters,
  fieldNumber,
  allowedDays,
  fromReport,
  setCenterValue,
  sowingTimeInSeconds,
  havestTimeInSeconds,
}) => {
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const areaUnit = useAreaUnit();
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] =
    useState('NDVI');
  const [expanded, setExpanded] = useState<number | null>(
    null
  );

  const [formattedDate, setFormattedDate] =
    useState<string>('');
  const [currentDayIndex, setCurrentDayIndex] = useState(0);

  const [selectedTiffIndex, setSelectedTiffIndex] =
    useState(0); // Track selected TIFF index

  const indexSelected = 'NDVI';
  const endTime = useMemo(() => {
    return havestTimeInSeconds === 0
      ? Math.floor(Date.now() / 1000)
      : havestTimeInSeconds;
  }, [havestTimeInSeconds]);

  const queryFilters = useMemo(() => {
    return {
      commonFilters,
      selectedIndex,
      startTime: sowingTimeInSeconds,
      endTime,
      fieldNumbers: [fieldNumber],
      wsId,
    };
  }, [
    selectedIndex,
    sowingTimeInSeconds,
    endTime,
    fieldNumber,
    wsId,
  ]);
  useEffect(() => {
    if (allowedDays[0] !== undefined) {
      const selectedDateTime = allowedDays[0];
      if (selectedDateTime) {
        const formattedDate = dayjs
          .unix(selectedDateTime?.indexTime)
          .format('DD/MM/YYYY');
        setFormattedDate(formattedDate);
      }
    }
  }, [allowedDays]);

  const {
    data: fieldDataForFieldNos,
    isFetching: isLoadingDataForFieldNos,
  } = useGetFieldDataForFieldNosQuery(queryFilters, {
    skip: !fieldNumber,
  });

  const { data: fieldLogs, refetch } =
    useGetFieldLogsByFieldNumberQuery(
      {
        fieldNo: fieldNumber,
        wsId,
      },
      {
        skip: !fieldNumber,
      }
    );
  const { data: checkinHistory } =
    useGetCheckInHistoryQuery(
      {
        fieldNo: fieldNumber,
        wsId,
      },
      {
        skip: !fieldNumber,
      }
    );

  const reportData: any[] = [];
  const parentSeedData: any[] = [];
  const inspectionVisitData: any[] = [];
  const pldData: any[] = [];

  if (
    fieldLogs?.data?.GetFieldLogsByFieldNumber
      ?.fieldAttributes
  ) {
    fieldLogs.data?.GetFieldLogsByFieldNumber.fieldAttributes.forEach(
      (attr: any) => {
        if (attr.attType === 'FIELD_ATT_REPORT') {
          reportData.push({
            ...attr.reportData,
          });
        } else if (attr.flData) {
          const createdActor = attr.flData?.flCreatedActor;
          const createdTime = attr.flData?.flTime;

          switch (attr.flData.flType) {
            case 'FL_PARENT_SEED_DATA':
              parentSeedData.push({
                ...attr.flData.flValue?.parentSeedData,
                flCreatedActor: createdActor,
                flCreatedTime: createdTime,
              });
              break;
            case 'FL_INSPECTION_VISIT':
              inspectionVisitData.push({
                ...attr.flData.flValue?.inspVisitValue,
                flCreatedActor: createdActor,
                flCreatedTime: createdTime,
              });
              break;
            default:
              pldData.push({
                ...attr.flData.flValue?.pldValue,
                flCreatedActor: createdActor,
                flCreatedTime: createdTime,
              });
              break;
          }
        }
      }
    );
  }

  const [reportDataState, setReportDataState] = useState<
    any[]
  >([]);

  // Merge all into one "fieldLogData" like before
  const fieldLogData = [
    ...reportDataState.map((d: any) => ({
      type: 'Field Observation',

      details: d,
    })),
    ...inspectionVisitData.map((d: any) => ({
      type: 'Inspection Visit',

      details: d,
    })),
    ...pldData.map((d: any) => ({
      type: 'Plough Down Record',

      details: d,
    })),
    ...parentSeedData.map((d: any) => ({
      type: 'Parent Seed Data',

      details: d,
    })),
  ];
  const handleRefetch = async () => {
    const updatedData = await refetch();
    if (
      updatedData?.data?.GetFieldLogsByFieldNumber
        ?.fieldAttributes
    ) {
      const newReportData: any[] = [];

      updatedData?.data.GetFieldLogsByFieldNumber.fieldAttributes.forEach(
        (attr: any) => {
          if (attr.attType === 'FIELD_ATT_REPORT') {
            newReportData.push({ ...attr.reportData });
          }
        }
      );
      setReportDataState(newReportData);
    }
  };

  const openDialog = (value: boolean) => {
    setOpen(value);
  };

  const fieldData =
    // daySelection !== undefined
    //   ?
    fieldDataForFieldNos?.data?.GetFieldDataForFieldNos
      ?.fieldDataList?.[0];
  // : undefined;
  const fieldIndexList = fieldData?.fieldIndexList || [];
  const tiffFileUrls = fieldIndexList
    ?.map((item) => item?.presignedS3TiffUrl)
    .filter(Boolean);
  const geometryHex = fieldData?.fieldGeomData?.geometry;
  const averageIndices = fieldIndexList
    .map((item) => item?.averageIndex)
    .filter(Boolean);
  const cloudCoverPercentages = fieldIndexList
    .map((item) => item?.cloudCoverPercentage)
    .filter(Boolean);
  const polygon = geometryHex
    ? latLngConversion(geometryHex)
    : { geometry: [] };

  // Static map image URL (example, replace with actual static map image URL)
  const staticMapImage =
    'https://via.placeholder.com/200x200.png?text=Static+Map';

  useEffect(() => {
    if (allowedDays.length > 0 && fromReport) {
      // setDaySelection(allowedDays[0].day);
      // setInputValue(String(allowedDays[0].day));
      setCurrentDayIndex(0);
    }
  }, [allowedDays, fromReport]);

  const handleNextDay = () => {
    if (currentDayIndex < allowedDays.length - 1) {
      const nextIndex = currentDayIndex + 1;
      setCurrentDayIndex(nextIndex);
    }
  };

  const handlePreviousDay = () => {
    if (currentDayIndex > 0) {
      const prevIndex = currentDayIndex - 1;
      setCurrentDayIndex(prevIndex);
    }
  };

  useEffect(() => {
    if (allowedDays[currentDayIndex] && fromReport) {
      const selectedDateTime = allowedDays[currentDayIndex];

      const formattedDate = dayjs
        .unix(selectedDateTime?.indexTime)
        .format('DD/MM/YYYY');
      setFormattedDate(formattedDate);
    }
  }, [currentDayIndex, allowedDays]);

  // Handle TIFF image selection
  const handleSelectImage = (index: number) => {
    setSelectedTiffIndex(index);
  };

  // Get NDVI and cloud cover from field data (example values, adjust based on actual data)
  const ndviValue =
    //  fieldIndexList[selectedTiffIndex]?.averageIndex || 0;
    averageIndices[selectedTiffIndex] || 0;
  const cloudCover =
    cloudCoverPercentages[selectedTiffIndex] || 0;

  const DISCOUNT_TABS: TDiscountTab[] = [
    {
      label: 'Overview',
      tabIndex: '1',
      fragment: 'overview',
    },
    {
      label: 'Field log',
      tabIndex: '2',
      fragment: 'field-log',
    },
    {
      label: 'Check-in history',
      tabIndex: '3',
      fragment: 'check-in-history',
    },
  ];

  const { hash } = useLocation();

  const [activeTab, setActiveTab] = useState(
    DISCOUNT_TABS.find(
      (ele) => `#${ele.fragment}` == hash
    ) || DISCOUNT_TABS[0]
  );

  const handleChange = (newSelectedTab: TDiscountTab) => {
    if (newSelectedTab) {
      setActiveTab(newSelectedTab);
    }
  };

  useEffect(() => {
    if (
      fieldLogs?.data?.GetFieldLogsByFieldNumber
        ?.fieldAttributes
    ) {
      const newReportData: any[] = [];

      fieldLogs.data.GetFieldLogsByFieldNumber.fieldAttributes.forEach(
        (attr: any) => {
          if (attr.attType === 'FIELD_ATT_REPORT') {
            newReportData.push({ ...attr.reportData });
          }
        }
      );
      setReportDataState(newReportData);
    }
  }, [fieldLogs]);

  const CardHeader = ({ item }: { item: any }) => (
    <>
      <div className="info-row">
        <span className="label">Growth Stage</span>
        <span className="value">
          {item?.details?.cropGrowthStage}
        </span>
      </div>

      <div className="info-row">
        <span className="label">Submitted By</span>
        <span className="submitter">
          <div className="avatar">
            {item?.details?.flCreatedActor?.actorName
              ?.trim()
              .charAt(0)
              .toUpperCase()}
          </div>
          <span>
            {item?.details?.flCreatedActor?.actorName}
          </span>
        </span>
      </div>

      <div className="info-row">
        <span className="label">Submitted On</span>
        <span className="value">
          {dayjs
            .unix(item?.details?.flCreatedTime?.seconds)
            .format('D MMM YYYY')}
        </span>
      </div>
    </>
  );

  function PolygonCanvas({
    polygonCoords,
  }: {
    polygonCoords: any;
  }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
      const renderPolygons = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        if (!context)
          throw new Error(
            'Failed to get 2D canvas context'
          );

        // Canvas dimensions
        const canvasWidth = 500;
        const canvasHeight = 500;
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        // Transform all coordinates to EPSG:3857 (assuming input is EPSG:4326)
        const projectedPolygons = polygonCoords.map(
          (polygon) =>
            polygon.map((coord) =>
              proj4('EPSG:4326', 'EPSG:3857', [
                coord.lng,
                coord.lat,
              ])
            )
        );

        // Find the combined bounding box in projected coordinates
        const allProjXs = projectedPolygons.flatMap(
          (polygon) => polygon.map((coord) => coord[0])
        );
        const allProjYs = projectedPolygons.flatMap(
          (polygon) => polygon.map((coord) => coord[1])
        );
        const minProjX = Math.min(...allProjXs);
        const maxProjX = Math.max(...allProjXs);
        const minProjY = Math.min(...allProjYs);
        const maxProjY = Math.max(...allProjYs);

        // Scale factors to map projected coordinates to canvas
        const xScale = canvasWidth / (maxProjX - minProjX);
        const yScale = canvasHeight / (maxProjY - minProjY);

        // Clear the canvas
        context.clearRect(0, 0, canvasWidth, canvasHeight);

        // Draw each polygon
        projectedPolygons.forEach(
          (projectedPolygon, index) => {
            context.beginPath();
            projectedPolygon.forEach(
              ([x, y], coordIndex) => {
                const canvasX = (x - minProjX) * xScale;
                const canvasY = (maxProjY - y) * yScale; // Flip y-axis to match canvas
                if (coordIndex === 0) {
                  context.moveTo(canvasX, canvasY);
                } else {
                  context.lineTo(canvasX, canvasY);
                }
              }
            );
            context.closePath();

            // Style the polygon (different colors for each polygon to distinguish)
            context.strokeStyle =
              index === 0
                ? 'rgba(8, 14, 0, 0.18)' //rgba(8, 14, 0, 0.58) //
                : 'rgba(140, 240, 26, 1)'; // Blue for first, orange for second
            context.lineWidth = 3;
            context.fillStyle =
              index === 0
                ? 'rgba(140, 240, 26, 0.20)' //rgba(8, 14, 0, 0.58)
                : 'rgba(8, 14, 0, 0.3)'; //rgba(140, 240, 26, 0.18)
            context.fill();
            context.stroke();
          }
        );
      };

      renderPolygons();
    }, []);

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
          width: '80px',
          height: '80px',
          backgroundImage: `url(${mapStatic})`,
          borderRadius: '4px',
          padding: 1,
        }}>
        <canvas ref={canvasRef} />
      </Box>
    );
  }

  function InfoRow({
    label,
    value,
  }: {
    label: string;
    value: string;
  }) {
    return (
      <div className="info-row">
        <span className="label">{label}</span>
        <span className="value">{value}</span>
      </div>
    );
  }
  const InspectionCard = ({ item }: { item: any }) => {
    // Separate harvest data from other inspections
    const harvestInsps =
      item?.details?.inspValueList?.filter(
        (insp: any) =>
          insp.inspVisitValType === 'INSP_HARVEST_DATA'
      );
    const otherInsps = item?.details?.inspValueList?.filter(
      (insp: any) =>
        insp.inspVisitValType !== 'INSP_HARVEST_DATA'
    );

    return (
      <div>
        {otherInsps.length > 0 && (
          <div className="card-body">
            <CardHeader item={item} />
            <div className="section">
              <h4 className="section-title">
                Inspection Activity
              </h4>

              {otherInsps.map((insp: any, idx: number) => {
                switch (insp.inspVisitValType) {
                  case 'INSP_FIELD_FLAG':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={flagIcon} />
                          FLAGGING
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            Flag
                          </span>
                          <span className="flag ">
                            <FlagIcon
                              sx={{
                                color:
                                  insp.fieldFlagVal
                                    ?.status === 'FS_RED'
                                    ? 'red'
                                    : insp.fieldFlagVal
                                        ?.status ===
                                      'FS_ORANGE'
                                    ? 'orange'
                                    : insp.fieldFlagVal
                                        ?.status ===
                                      'FS_YELLOW'
                                    ? 'yellow'
                                    : insp.fieldFlagVal
                                        ?.status ===
                                      'FS_GREEN'
                                    ? 'green'
                                    : '',
                              }}
                            />
                            {insp.fieldFlagVal?.status ===
                            'FS_RED'
                              ? 'Red'
                              : insp.fieldFlagVal
                                  ?.status === 'FS_ORANGE'
                              ? 'Orange'
                              : insp.fieldFlagVal
                                  ?.status === 'FS_YELLOW'
                              ? 'Yellow'
                              : insp.fieldFlagVal
                                  ?.status === 'FS_GREEN'
                              ? 'Green'
                              : ''}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="label">
                            Reason
                          </span>
                          <span className="value">
                            {insp.fieldFlagVal?.reason}
                          </span>
                        </div>
                      </div>
                    );

                  case 'INSP_MANUAL_YIELD_ESTIMATE':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={yieldEstimateIcon} />
                          YIELD ESTIMATE
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            Count
                          </span>
                          <span className="value">
                            {
                              insp.manualYieldEstimateVal
                                ?.quantity
                            }{' '}
                            kg
                          </span>
                        </div>
                      </div>
                    );

                  case 'INSP_DETASELLING_FLAG':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={detasslingIcon} />
                          DE-TASSLING
                        </h5>

                        <div className="info-row">
                          <span className="label">
                            Flag
                          </span>
                          <span className="flag ">
                            <FlagIcon
                              sx={{
                                color:
                                  insp.detasellingFlagVal
                                    ?.flag === 'FS_RED'
                                    ? 'red'
                                    : insp
                                        .detasellingFlagVal
                                        ?.flag ===
                                      'FS_ORANGE'
                                    ? 'orange'
                                    : insp
                                        .detasellingFlagVal
                                        ?.flag ===
                                      'FS_YELLOW'
                                    ? 'yellow'
                                    : insp
                                        .detasellingFlagVal
                                        ?.flag ===
                                      'FS_GREEN'
                                    ? 'green'
                                    : '',
                              }}
                            />
                            {insp.detasellingFlagVal
                              ?.flag === 'FS_RED'
                              ? 'Red'
                              : insp.detasellingFlagVal
                                  ?.flag === 'FS_ORANGE'
                              ? 'Orange'
                              : insp.detasellingFlagVal
                                  ?.flag === 'FS_YELLOW'
                              ? 'Yellow'
                              : insp.detasellingFlagVal
                                  ?.flag === 'FS_GREEN'
                              ? 'Green'
                              : ''}
                          </span>
                        </div>

                        <div className="info-row">
                          <span className="label">
                            Date range
                          </span>
                          <span className="value">
                            {dayjs
                              .unix(
                                insp.detasellingFlagVal
                                  ?.dateRange?.startTime
                                  ?.seconds
                              )
                              .format('DD/MM/YYYY')}
                            {' - '}
                            {dayjs
                              .unix(
                                insp.detasellingFlagVal
                                  ?.dateRange?.endTime
                                  ?.seconds
                              )
                              .format('DD/MM/YYYY')}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="label">
                            Status
                          </span>
                          <span className="status closeout">
                            <img src={statusIcon} />
                            {insp.detasellingFlagVal
                              ?.status === 'DT_FIRST_PASS'
                              ? 'First Pass'
                              : insp.detasellingFlagVal
                                  ?.status ===
                                'DT_SECOND_PASS'
                              ? 'Second Pass'
                              : insp.detasellingFlagVal
                                  ?.status ===
                                'DT_THIRD_PASS'
                              ? 'Third Pass'
                              : insp.detasellingFlagVal
                                  ?.status ===
                                'DT_CLOSE_OUT'
                              ? 'Close Out'
                              : ''}
                          </span>
                        </div>
                      </div>
                    );
                  case 'INSP_CULTIVATION_METHOD':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={cultivationMethod} />
                          CULTIVATION METHOD
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            Method
                          </span>
                          <span className="value">
                            {insp.cultMethodVal?.cmValue}
                          </span>
                        </div>
                      </div>
                    );
                  case 'INSP_STAGGERING':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={staggering} />
                          STAGGERING
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            Value
                          </span>
                          <span className="value">
                            {insp.stagVal?.staggeringVal}
                          </span>
                        </div>
                      </div>
                    );
                  case 'INSP_POPULATION_COUNT':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={populationCount} />
                          POPULATION COUNT
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            Male population
                          </span>
                          <span className="value">
                            {
                              insp.popCountVal
                                ?.malePopulation
                            }
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="label">
                            Female population
                          </span>
                          <span className="value">
                            {
                              insp.popCountVal
                                ?.femalePopulation
                            }
                          </span>
                        </div>
                      </div>
                    );
                  case 'INSP_CROP_RATING':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={cropRating} />
                          CROP RATING
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            Rating
                          </span>
                          <span className="value">
                            {insp.cropRatingVal?.cropRating}
                          </span>
                        </div>
                      </div>
                    );
                  case 'INSP_PREV_CROP':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={yieldEstimateIcon} />
                          PREV CROP
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            value
                          </span>
                          <span className="value">
                            {insp.prevCropVal?.pcValue}
                          </span>
                        </div>
                      </div>
                    );
                  case 'INSP_ROW_RATIO':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={rowRatio} />
                          ROW RATIO
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            value
                          </span>
                          <span className="value">
                            {insp.rowRatioVal?.rowRatioVal}
                          </span>
                        </div>
                      </div>
                    );
                  case 'INSP_ISOLATION':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={isolationDistance} />
                          INSPECTION ISOLATION
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            value
                          </span>
                          <span className="value">
                            {insp.isoVal?.isolationSource}
                          </span>
                        </div>
                      </div>
                    );
                  case 'INSP_SEED_SET':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={yieldEstimateIcon} />
                          INSPECTION SEED SET
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            value
                          </span>
                          <span className="value">
                            {insp.seedSetVal?.seedSetPerc}
                          </span>
                        </div>
                      </div>
                    );
                  case 'INSP_ROUGHING':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={yieldEstimateIcon} />
                          INSPECTION ROUGHING
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            value
                          </span>
                          <span className="value">
                            {insp.roughingVal?.roughingVal}
                          </span>
                        </div>
                      </div>
                    );
                  case 'INSP_NICK_STATUS':
                    return (
                      <div
                        className="sub-section"
                        key={idx}>
                        <h5 className="sub-title flex items-center gap-2">
                          <img src={nickStatus} />
                          INSPECTION NICK STATUS
                        </h5>
                        <div className="info-row">
                          <span className="label">
                            value
                          </span>
                          <span className="value">
                            {insp.nickStatusVal?.nickStatus}
                          </span>
                        </div>
                      </div>
                    );
                  default:
                    return null;
                }
              })}
            </div>
          </div>
        )}

        {/* Render harvest separately, each one has its own header */}
        {harvestInsps.map((insp: any, idx: number) => (
          <div className="card-body" key={idx}>
            <div className="info-row">
              <span className="label">Growth Stage</span>
              <span className="value">
                {item?.details?.cropGrowthStage}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Lot no</span>
              <span className="value">
                {fieldData?.fieldDetails?.lotNo}
              </span>
            </div>

            <div className="info-row">
              <span className="label">Submitted By</span>
              <span className="submitter">
                <div className="avatar">
                  {item?.details?.flCreatedActor?.actorName
                    ?.trim()
                    .charAt(0)
                    .toUpperCase()}
                </div>
                <span>
                  {item?.details?.flCreatedActor?.actorName}
                </span>
              </span>
            </div>

            <div className="info-row">
              <span className="label">Harvested On</span>
              <span className="value">
                {dayjs
                  .unix(
                    insp?.harvestDataVal?.batches?.[0]
                      ?.harvestTime?.seconds
                  )
                  .format('D MMM YYYY')}
              </span>
            </div>

            <div className="section">
              <h4 className="sub-title flex items-center gap-2">
                <img src={harvestIcon} />
                HARVEST COLLECTION
              </h4>

              <div className="sub-section">
                {insp?.harvestDataVal?.batches?.map(
                  (element, index) => (
                    <>
                      <h5 className="sub-title-batch">
                        {`BATCH ` + index + 1}
                      </h5>
                      <div className="info-row">
                        <span className="label">
                          No. of Packages
                        </span>
                        <span className="value">
                          {element?.packageNo}
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="label">
                          Total Quantity
                        </span>
                        <span className="value">
                          {element?.totalQty}
                        </span>
                      </div>

                      <div className="info-row">
                        <span className="label">
                          Harvested Area
                        </span>
                        <span className="value">
                          {element?.harvestedArea}
                        </span>
                      </div>

                      <div className="info-row">
                        <span className="label">
                          Transport Number
                        </span>
                        <span className="value">
                          {element?.transportNo}
                        </span>
                      </div>
                    </>
                  )
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };
  const FieldDataCard = ({ fieldDetails }: any) => {
    const {
      region,
      village,
      grower,
      crop,
      hybrid,
      tpm,
      regionalManager,
    } = fieldDetails || {};

    const rows = [
      { label: 'Region', value: region },
      { label: 'Village', value: village },
      { label: 'Grower', value: grower },
      { label: 'Crop', value: crop },
      { label: 'Hybrid', value: hybrid },
      { label: 'TPM', value: tpm },
      { label: 'Regional Manager', value: regionalManager },
    ];

    return (
      <Box
        sx={{
          border: '1px solid #E5E7EB',
          // borderRadius: 2,
          padding: 2,
          backgroundColor: '#fff',
          mb: 1,
        }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 14,
            mb: 1,
          }}>
          Field Data
        </Typography>
        <Divider sx={{ mb: 1 }} />

        <Grid container spacing={1}>
          {rows.map(
            (item) =>
              item.value && (
                <React.Fragment key={item.label}>
                  <Grid item xs={6} sm={4}>
                    <Typography
                      sx={{
                        color: 'rgba(0,0,0,0.7)',
                        fontSize: 13,
                        fontWeight: 400,
                      }}>
                      {item.label}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={8}>
                    <Typography
                      sx={{
                        color: 'rgba(0,0,0,0.9)',
                        fontWeight: 600,
                        fontSize: 13,
                      }}>
                      {item.value}
                    </Typography>
                  </Grid>
                </React.Fragment>
              )
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <>
      {isLoadingDataForFieldNos && <AppCircularProgress />}

      {/* <div
        style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          backgroundColor: '#ececec',
        }}> */}
      {/* <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          height: '100vh', // 👈 full viewport height
          width: '100vw', // 👈 full viewport width
          backgroundColor: '#ececec',
          overflow: 'hidden',
        }}> */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          height: '92vh', // 👈 full viewport height
          width: '96vw', // 👈 full viewport width
          overflowX: 'hidden', // ❌ no horizontal scroll
          overflowY: 'hidden', // parent shouldn't scroll vertically
          backgroundColor: '#ececec',
        }}>
        <div
          style={{
            flex: '0 0 30%',
            height: '97%',
            overflowY: 'auto', // ✅ vertical scroll only
            overflowX: 'hidden', // ❌ no horizontal scroll
            backgroundColor: '#ececec',
            boxSizing: 'border-box',
          }}>
          <CustomTab<TDiscountTab, keyof TDiscountTab>
            tabList={DISCOUNT_TABS}
            activeTab={activeTab}
            valueKey={'tabIndex'}
            labelKey={'label'}
            onTabChange={handleChange}
            fontsize={13}
            tabPanel={(tab) => {
              switch (tab.tabIndex) {
                case '1':
                  return (
                    <div style={{ marginTop: -15 }}>
                      <div
                        style={{
                          marginLeft: -25,
                          width: '40vw',
                        }}>
                        <FieldDataCard
                          fieldDetails={{
                            region:
                              fieldData?.fieldDetails
                                ?.geographyData?.region,
                            village:
                              fieldData?.fieldDetails
                                ?.geographyData?.village,
                            grower:
                              fieldData?.fieldDetails
                                ?.growerName,
                            crop: fieldData?.fieldDetails
                              ?.crop,
                            hybrid:
                              fieldData?.fieldDetails
                                ?.hybridCode,
                            tpm: fieldData?.fieldDetails
                              ?.employeeData?.tpm,
                            regionalManager:
                              fieldData?.fieldDetails
                                ?.employeeData
                                ?.regionManager,
                          }}
                        />
                      </div>

                      <div
                        style={{
                          width: '30vw',
                          backgroundColor: '#fff',
                          marginLeft: -25,
                          padding: 12,
                          height: 520,
                        }}>
                        <TimeSeriesPlot
                          fieldId={fieldNumber}
                          sowingTimeInSeconds={
                            sowingTimeInSeconds
                          }
                          havestTimeInSeconds={
                            havestTimeInSeconds
                          }
                          selectedIndex={selectedIndex}
                        />
                      </div>
                    </div>
                  );
                case '2':
                  return (
                    <div
                      style={{
                        width: '29vw',
                        marginLeft: -25,
                        marginTop: -15,
                        height: 800, // same height as your main box
                        // backgroundColor: '#fff',
                      }}>
                      <div
                        style={{
                          marginLeft: 15,
                          marginBottom: 5,
                          fontSize: 12,
                          color: 'rgba(0, 0, 0, 0.9)',
                          fontWeight: 'bold',
                        }}>
                        All logs (
                        {fieldLogData?.length || 0})
                      </div>

                      {fieldLogData?.length > 0 ? (
                        <Grid>
                          <Grid item xs={12} md={12}>
                            <Box
                              sx={{
                                // width: 450,
                                //   p: 2,
                                backgroundColor: '#ececec',
                              }}>
                              {fieldLogData.map(
                                (item, index) => (
                                  <Accordion
                                    key={index}
                                    expanded={
                                      expanded === index
                                    }
                                    onChange={() =>
                                      setExpanded(
                                        expanded === index
                                          ? null
                                          : index
                                      )
                                    }
                                    sx={{
                                      backgroundColor:
                                        '#fff',
                                      boxShadow: 'none',
                                      // border:
                                      //   '1px solid #E5E7EB',
                                      //  borderRadius: '12px',
                                      mb: 1.5,
                                    }}>
                                    <AccordionSummary
                                      expandIcon={
                                        <ExpandMoreIcon
                                          sx={{
                                            color:
                                              'rgba(0, 105, 112, 1)',
                                          }}
                                        />
                                      }
                                      sx={{
                                        display: 'flex',
                                        justifyContent:
                                          'space-between',
                                        alignItems:
                                          'center',
                                        '& .MuiAccordionSummary-content':
                                          {
                                            margin: 0,
                                            display: 'flex',
                                            justifyContent:
                                              'space-between',
                                            width: '100%',
                                            alignItems:
                                              'center',
                                            height: 62,
                                          },
                                      }}>
                                      {/* Left section */}

                                      <Box
                                        sx={{
                                          display: 'flex',
                                          flexDirection:
                                            'column',
                                          gap: 0.5,
                                        }}>
                                        <Typography
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: 13,
                                            display: 'flex',
                                            alignItems:
                                              'center',
                                            gap: 1,
                                          }}>
                                          {item?.type}

                                          {/* Requested Chip */}
                                          {((item?.type ===
                                            'Field Observation' &&
                                            item?.details
                                              ?.reportStatus ===
                                              'REPORT_REQUESTED') ||
                                            (item?.type ===
                                              'Plough Down Record' &&
                                              item?.details
                                                ?.approvalStatus ===
                                                'PLD_PENDING')) && (
                                            <Chip
                                              label="Requested"
                                              sx={{
                                                height: 24,
                                                fontSize: 11,
                                                borderRadius:
                                                  '16px',
                                                borderColor:
                                                  'rgba(0, 105, 112, 1)',
                                                border:
                                                  '0.6px solid',
                                                backgroundColor:
                                                  'rgba(0, 105, 112, 0.1)',
                                                color:
                                                  'rgba(0, 105, 112, 1)',
                                                fontWeight: 600,
                                              }}
                                            />
                                          )}
                                        </Typography>
                                        {!(
                                          expanded === index
                                        ) && (
                                          <Box
                                            sx={{
                                              display:
                                                'flex',
                                              alignItems:
                                                'center',
                                              gap: 1,
                                              color:
                                                '#5E718D',
                                              fontSize: 12,
                                              fontWeight: 600,
                                            }}>
                                            <Avatar
                                              sx={{
                                                bgcolor:
                                                  '#E9D7FF',
                                                color:
                                                  '#6F42C1',
                                                width: 20,
                                                height: 20,
                                                fontSize: 12,
                                              }}>
                                              {item?.type ===
                                              'Field Observation'
                                                ? item?.details?.createdActor?.actorName
                                                    ?.trim()
                                                    .charAt(
                                                      0
                                                    )
                                                    .toUpperCase()
                                                : item?.details?.flCreatedActor?.actorName
                                                    ?.trim()
                                                    .charAt(
                                                      0
                                                    )
                                                    .toUpperCase()}
                                            </Avatar>
                                            <Typography
                                              sx={{
                                                fontSize: 12,
                                                fontStyle:
                                                  'italic',
                                                color:
                                                  'rgba(0,0,0,0.7)',
                                              }}>
                                              {item?.type ===
                                              'Field Observation'
                                                ? item
                                                    ?.details
                                                    ?.createdActor
                                                    ?.actorName
                                                : item
                                                    ?.details
                                                    ?.flCreatedActor
                                                    ?.actorName}
                                            </Typography>
                                            <Box
                                              sx={{
                                                width: 4,
                                                height: 4,
                                                borderRadius:
                                                  '50%',
                                                bgcolor:
                                                  '#9CA3AF',
                                              }}
                                            />
                                            <Typography
                                              sx={{
                                                fontSize: 12,
                                                fontStyle:
                                                  'italic',
                                                color:
                                                  'rgba(0,0,0,0.7)',
                                              }}>
                                              {item?.type ===
                                              'Field Observation'
                                                ? dayjs
                                                    .unix(
                                                      item
                                                        ?.details
                                                        ?.createdTime
                                                        ?.seconds
                                                    )
                                                    .format(
                                                      'D MMM YYYY'
                                                    )
                                                : dayjs
                                                    .unix(
                                                      item
                                                        ?.details
                                                        ?.flCreatedTime
                                                        ?.seconds
                                                    )
                                                    .format(
                                                      'D MMM YYYY'
                                                    )}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>

                                      {/* Right section — View detail link */}
                                      <Link
                                        sx={{
                                          color:
                                            'rgba(0, 105, 112, 1)',
                                          textDecoration:
                                            'none',
                                          fontWeight: 500,
                                          fontSize: 14,
                                          ml: 2,
                                          '&:hover': {
                                            textDecoration:
                                              'underline',
                                          },
                                        }}>
                                        {expanded === index
                                          ? 'Hide Detail'
                                          : 'View Detail'}
                                      </Link>
                                    </AccordionSummary>
                                    <AccordionDetails
                                      sx={{
                                        marginTop: -4,
                                      }}>
                                      <div
                                        style={{
                                          borderBottom:
                                            '1px solid  #ececec',
                                          marginBottom: 10,
                                          marginTop: -8,
                                        }}></div>
                                      {item?.type ===
                                      'Inspection Visit' ? (
                                        <InspectionCard
                                          item={item}
                                        />
                                      ) : item?.type ===
                                        'Plough Down Record' ? (
                                        <div className="w-full max-w-md    p-4 bg-white">
                                          {/* Header */}

                                          {/* Field Area with Image */}
                                          <div className="mb-4">
                                            <span className="block text-sm text-gray-500">
                                              Field Area
                                            </span>
                                            {/* <div className="flex items-center space-x-3 mt-1"> */}
                                            <PolygonCanvas
                                              polygonCoords={[
                                                item
                                                  ?.details
                                                  ?.pldGeometry
                                                  ? latLngConversion(
                                                      item
                                                        ?.details
                                                        ?.pldGeometry
                                                    )
                                                      ?.geometry
                                                  : null,
                                                item
                                                  ?.details
                                                  ?.geometry
                                                  ? latLngConversion(
                                                      item
                                                        ?.details
                                                        ?.geometry
                                                    )
                                                      ?.geometry
                                                  : null,
                                              ].filter(
                                                Boolean
                                              )}
                                            />
                                            {/* </div> */}
                                          </div>

                                          {/* Details */}
                                          <div className="space-y-3">
                                            <InfoRow
                                              label="Field Area"
                                              value={`${fieldData?.fieldDetails?.geofenceArea?.toFixed(
                                                2
                                              )} ${areaUnit}`}
                                            />
                                            <InfoRow
                                              label="Date of Ploughing"
                                              value={dayjs
                                                .unix(
                                                  item
                                                    ?.details
                                                    ?.pldTime
                                                    ?.seconds
                                                )
                                                .format(
                                                  'D MMM YYYY'
                                                )}
                                            />
                                            <InfoRow
                                              label="Ploughed Area"
                                              value={`${item?.details?.pldArea?.toFixed(
                                                2
                                              )} ${areaUnit}`}
                                            />
                                            <InfoRow
                                              label="Remarks"
                                              value={
                                                item
                                                  ?.details
                                                  ?.remarks
                                              }
                                            />
                                            <InfoRow
                                              label="Sowing Date"
                                              value={
                                                fieldData
                                                  ?.fieldDetails
                                                  ?.sowingTime
                                                  ?.seconds >
                                                0
                                                  ? dayjs
                                                      .unix(
                                                        fieldData
                                                          ?.fieldDetails
                                                          ?.sowingTime
                                                          ?.seconds
                                                      )
                                                      .format(
                                                        'DD MM YYYY'
                                                      )
                                                  : '-'
                                              }
                                            />
                                            <InfoRow
                                              label="Crop"
                                              value={
                                                fieldData
                                                  ?.fieldDetails
                                                  ?.crop
                                              }
                                            />
                                            <InfoRow
                                              label="Hybrid"
                                              value={
                                                fieldData
                                                  ?.fieldDetails
                                                  ?.hybridCode
                                              }
                                            />
                                            <InfoRow
                                              label="Village"
                                              value={
                                                fieldData
                                                  ?.fieldDetails
                                                  ?.geographyData
                                                  ?.village
                                              }
                                            />
                                          </div>

                                          {/* Submitted By */}
                                          <div className="info-row">
                                            <span className="label">
                                              Submitted By
                                            </span>
                                            <span className="submitter">
                                              <div className="avatar">
                                                {item?.details?.flCreatedActor?.actorName
                                                  ?.trim()
                                                  .charAt(0)
                                                  .toUpperCase()}
                                              </div>
                                              <span>
                                                {
                                                  item
                                                    ?.details
                                                    ?.flCreatedActor
                                                    ?.actorName
                                                }
                                              </span>
                                            </span>
                                          </div>
                                        </div>
                                      ) : item?.type ===
                                        'Field Observation' ? (
                                        <div
                                        // style={{
                                        //   maxHeight:
                                        //     '400px',

                                        // }}
                                        >
                                          <ReportsCard
                                            fieldId={
                                              fieldNumber
                                            }
                                            thread={
                                              item?.details
                                            }
                                            handleRefetch={
                                              handleRefetch
                                            }
                                          />
                                        </div>
                                      ) : (
                                        <ParentSeedData
                                          parentSeedData={
                                            item?.details
                                          }
                                        />
                                      )}
                                    </AccordionDetails>
                                  </Accordion>
                                )
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      ) : (
                        <div
                          style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            color: '#666',
                            fontSize: 11,
                            lineHeight: 2,
                          }}>
                          <p
                            style={{
                              fontWeight: 'bold',
                              margin: 0,
                            }}>
                            No data
                          </p>
                          <p style={{ margin: 0 }}>
                            Data will appear here once
                            available.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                case '3':
                  return (
                    <div
                      style={{
                        backgroundColor: '#fff',
                        marginTop: -15,
                        padding: 15,
                        width: '40vw',
                        marginLeft: -25,
                        height: '100vh',
                      }}>
                      {checkinHistory?.data
                        ?.GetCheckInHistory?.checkInList
                        ?.length > 0 ? (
                        checkinHistory.data.GetCheckInHistory.checkInList.map(
                          (checkIn, idx) => {
                            const date = dayjs
                              .unix(
                                checkIn.actorData.actorTime
                                  .seconds
                              )
                              .format('D MMM YYYY');
                            const name =
                              checkIn.actorData.actorId
                                .actorName;

                            return (
                              <div
                                key={idx}
                                className="flex gap-4">
                                {/* Dot + Line */}
                                <div className="flex flex-col items-center self-stretch">
                                  <div className="w-3.5 h-3.5 rounded-full border-[1.5px] border-[#006970] bg-white"></div>

                                  {idx !==
                                    checkinHistory.data
                                      .GetCheckInHistory
                                      .checkInList.length -
                                      1 && (
                                    <div className="w-px flex-1 bg-[#E5E5E6]/50"></div>
                                  )}
                                </div>

                                {/* Right Content (with spacing) */}
                                <div className="flex flex-col pb-8">
                                  <p className="text-[12px] italic text-[#6C6C6C]">
                                    {date}
                                  </p>

                                  <div className="flex items-center gap-2 mt-2">
                                    <Avatar
                                      sx={{
                                        width: 30,
                                        height: 30,
                                      }}
                                    />
                                    <p className="text-[15px] font-medium text-[#000]">
                                      {name}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        )
                      ) : (
                        <div
                          style={{
                            marginLeft: -200,
                            height: '80vh',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}>
                          <div
                            style={{
                              textAlign: 'center',
                              color: '#666',
                              fontSize: 11,
                              lineHeight: 2,
                            }}>
                            <p
                              style={{
                                fontWeight: 'bold',
                              }}>
                              No data
                            </p>
                            <p>
                              Data will appear here once
                              available.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );

                default:
                  return '';
              }
            }}
          />
        </div>
        {/* <div
          style={{
            marginTop: 25,
            marginLeft: -110,
            // width: '70%',
            width: 1140,
          }}> */}
        {/* <div
          style={{
            flex: '1 1 70%',
            height: '100%',
            overflow: 'hidden',
            marginTop: 25,
            //  padding: '8px',
            boxSizing: 'border-box',
          }}> */}
        <div
          style={{
            flex: '1 1 70%',
            height: '105%',
            // overflowY: 'auto', // ✅ vertical scroll only
            overflowX: 'hidden', // ❌ no horizontal scroll
            marginTop: 25,
            boxSizing: 'border-box',
          }}>
          <Grid
            key={fieldNumber}
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            //style={{ height: '950px', padding: '2px' }}
            style={{ height: '100%', width: '100%' }}>
            <Box
              className="grid-item-child"
              sx={{ height: '100%' }}>
              {!fieldNumber ? (
                <Box className="no-farm-selected">
                  No farm selected
                </Box>
              ) : (
                <Box sx={{ height: '100%' }}>
                  {!isLoadingDataForFieldNos &&
                    //   allowedDays[0] === undefined ? (
                    //   <Box className="no-day-selection">
                    //     No Data Available
                    //   </Box>
                    // ) :
                    //tiffFileUrls.length === 0 ||
                    (!geometryHex ? (
                      <Box className="no-data-available">
                        {
                          // tiffFileUrls.length === 0
                          //   ? 'No Data Available For This Index'
                          //   :
                          'Polygon data not available'
                        }
                      </Box>
                    ) : (
                      <Box className="map-container">
                        {!isLoadingDataForFieldNos && (
                          <>
                            <GoogleMapView
                              tiffFileUrls={[
                                tiffFileUrls[
                                  selectedTiffIndex
                                ],
                              ]}
                              geometryHex={geometryHex}
                              indexName={selectedIndex}
                              setCenterValue={
                                setCenterValue
                              }
                              selectedDate={
                                formattedDate
                                  ? dayjs(
                                      formattedDate,
                                      'DD/MM/YYYY'
                                    )
                                  : undefined
                              }
                              fieldIndexList={
                                fieldIndexList[
                                  selectedTiffIndex
                                ]
                              }
                              commonFilters={commonFilters}
                              cloudCover={cloudCover}
                              ndviValue={ndviValue}
                              indexSelected={indexSelected}
                              setSelectedIndex={
                                setSelectedIndex
                              }
                              selectedIndex={selectedIndex}
                            />
                            <StaticTiffImages
                              tiffFileUrls={tiffFileUrls}
                              polygon={
                                polygon.geometry as polygonType[]
                              }
                              indexName={selectedIndex}
                              onSelectImage={
                                handleSelectImage
                              }
                              selectedIndex={
                                selectedTiffIndex
                              }
                              staticMapImage={
                                staticMapImage
                              }
                              allowedDays={allowedDays}
                            />
                          </>
                        )}
                      </Box>
                    ))}
                  {fromReport && (
                    <Box className="pagination-controls">
                      <IconButton
                        onClick={handlePreviousDay}
                        disabled={currentDayIndex === 0}>
                        &lt;
                      </IconButton>
                      <span>
                        {allowedDays[currentDayIndex]?.day}
                      </span>
                      <IconButton
                        onClick={handleNextDay}
                        disabled={
                          currentDayIndex ===
                          allowedDays.length - 1
                        }>
                        &gt;
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </div>
      </div>
      {open && geometryHex && tiffFileUrls.length > 0 && (
        <CustomDialog
          maxWidth="xl"
          open={open}
          setOpen={openDialog}
          dialogTitle={`Farm ${fieldData.fieldNumber}`}>
          <MapOnDialogView
            geometryHex={geometryHex}
            tiffFileUrls={tiffFileUrls}
            indexName={selectedIndex}
          />
        </CustomDialog>
      )}
    </>
  );
};
